/**
 * Copyright IBM Corp. 2022, 2026
 */

import { Suspense, useEffect, useRef, useState } from 'react'
import Image from 'next/image'
import { type IntegrationsFieldsFragment } from 'types/dato'
import * as ProductLogoMarks from 'lib/mktg-logos/all-product-logo-marks_on-light'
import { MDSButton, Tabs } from '@hashicorp/mds-react/components'
import { TabsHandler } from 'components/materia/tabs/components/tabs-handler'
import { Layout } from '@hashicorp/mds-react'
import s from './integrations-tabs.module.css'

function capitalizeFirstLetter(str) {
	return str.charAt(0).toUpperCase() + str.slice(1)
}

export interface IIntegrationsTabs {
	groupedIntegrations: IGroupedIntegrations
	panels: {
		name: string
	}[]
}
interface IGroupedIntegrations {
	[product: string]: IntegrationsFieldsFragment[]
}

export const IntegrationsTabs = ({
	groupedIntegrations,
	panels,
}: IIntegrationsTabs) => {
	const panelLcNames = panels.map((panel): { name: string } => {
		return { name: capitalizeFirstLetter(panel.name) }
	})
	return (
		<Layout className={s.mainContent} paddingTop={'small'}>
			<Suspense>
				<TabsHandler panels={panelLcNames}>
					{panels.map((product) => {
						const integrations = groupedIntegrations[product.name]
						return (
							<Tabs.Panel key={product.name}>
								<>
									{integrations.map((integration) => (
										<Integration
											product={
												integration.integrationProduct.products[0].product
											}
											integrationName={integration.name}
											key={integration.id}
											description={integration.description}
											getStartedLink={integration.gettingStartedUrl}
											resources={integration.links.map((link) => ({
												label: link.title,
												value: link.id,
												type: link.external ? 'outbound' : 'inbound',
												href: link.url,
											}))}
										/>
									))}
								</>
							</Tabs.Panel>
						)
					})}
				</TabsHandler>
			</Suspense>
		</Layout>
	)
}

function Integration({
	product,
	integrationName,
	description,
	resources,
	getStartedLink,
}: {
	product: string
	integrationName: string
	description: string
	resources: DropdownOption[]
	getStartedLink: string
}) {
	return (
		<div className={s.integrationItem}>
			<h4 className={s.integrationTitle}>
				<Image
					src={ProductLogoMarks[product.toLowerCase()]}
					className={s.integrationLogo}
					alt={product}
					width={144}
					height={144}
				/>
				{integrationName}
			</h4>
			<p>{description}</p>
			<div className={s.bottom}>
				{getStartedLink && (
					<MDSButton title="Get started" size="medium" url={getStartedLink} />
				)}
				{resources.length > 0 && (
					<Dropdown title="Resources" options={resources} />
				)}
			</div>
		</div>
	)
}

type DropdownOption = {
	label: string
	value: string
	type?: 'inbound' | 'outbound' | 'download'
	href?: string
}

function Dropdown({
	options,
	onChange,
	title,
}: {
	options: DropdownOption[]
	onChange?: (selection: string) => void
	title: string
}): React.ReactElement {
	const [open, setOpen] = useState(false)
	const buttonRef = useRef<HTMLDivElement>()

	useEffect(() => {
		function handleClickOutside(event) {
			if (buttonRef.current?.contains(event.target)) return

			setOpen(false)
		}
		document.addEventListener('mousedown', handleClickOutside)

		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
		}
	}, [])

	return (
		<div className={s.dropdownRoot} ref={buttonRef}>
			<MDSButton
				className={s.trigger}
				onClick={() => setOpen((open) => !open)}
				aria-expanded={open}
				aria-controls="menu-list"
				aria-haspopup="true"
				data-testid="version-dropdown"
				theme={{
					brand: 'neutral',
					variant: 'secondary',
				}}
				title={title}
				icon="chevron-down"
				iconPosition="trailing"
			/>
			{open && (
				<ul className={s.menu} id="menu-list" role="menu">
					{options.map((option) => (
						<li
							className={s.option}
							tabIndex={0}
							role="menuitem"
							key={option.value}
							onClick={() => {
								onChange?.(option.value)
								setOpen(false)
							}}
						>
							<MDSButton
								title={option.label}
								size="medium"
								linkType={option.type}
								theme={{
									brand: 'neutral',
									variant: 'tertiary',
								}}
								url={option.href}
							/>
						</li>
					))}
				</ul>
			)}
		</div>
	)
}
